'use client';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { useColorScheme } from '@mui/material/styles';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import React from 'react';

export interface ColorSchemeToggleProps {
  /** `menu`: full-width row for dropdowns. `toolbar`: responsive. `footer`: compact for the footer bar. */
  layout?: 'toolbar' | 'menu' | 'footer';
}

export default function ColorSchemeToggle({
  layout = 'toolbar',
}: ColorSchemeToggleProps) {
  const { mode, setMode } = useColorScheme();
  const isMenu = layout === 'menu';
  const isFooter = layout === 'footer';

  // Picks a value based on the active layout, falling back to the toolbar value.
  const pickByLayout = <Footer, Menu, Toolbar>(
    footerValue: Footer,
    menuValue: Menu,
    toolbarValue: Toolbar,
  ): Footer | Menu | Toolbar => {
    if (isFooter) return footerValue;
    if (isMenu) return menuValue;
    return toolbarValue;
  };

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: 'light' | 'dark' | 'system' | null,
  ) => {
    if (value != null) {
      setMode(value);
    }
  };

  return (
    <ToggleButtonGroup
      exclusive
      value={mode ?? 'system'}
      onChange={handleChange}
      size="small"
      aria-label="Theme mode"
      sx={{
        mr: isFooter ? { xs: 0, sm: '20px' } : '20px',

        width: pickByLayout('auto', '100%', { xs: '100%', md: 'auto' }),
        minHeight: isFooter ? 0 : undefined,
        '& .MuiToggleButton-root': {
          flex: pickByLayout('initial', 1, { xs: 1, md: 'initial' }),
          px: pickByLayout(0.5, 0, { xs: 0, md: 1 }),
          py: pickByLayout(0.125, 0.25, 0.5),
          minWidth: isFooter ? 0 : undefined,
          '& .MuiSvgIcon-root': {
            fontSize: pickByLayout('14px', '1rem', undefined),
          },
        },
      }}
    >
      <Tooltip title="Match system">
        <ToggleButton value="system" aria-label="Use system color scheme">
          <SettingsBrightnessOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Light">
        <ToggleButton value="light" aria-label="Light mode">
          <LightModeOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Dark">
        <ToggleButton value="dark" aria-label="Dark mode">
          <DarkModeOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );
}
